import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import shift components (lazy-loaded for performance)
const ShiftsList = React.lazy(() => import("./ShiftsList"));
const NewShift = React.lazy(() => import("./NewShift"));
const ShiftDetails = React.lazy(() => import("./ShiftDetails"));
const RosterDashboard = React.lazy(() => import("./RosterDashboard"));
const MyShifts = React.lazy(() => import("./MyShifts"));
const OpenShifts = React.lazy(() => import("./OpenShifts"));
const EmployeeHours = React.lazy(() => import("./EmployeeHours"));
const ShiftSettings = React.lazy(() => import("./ShiftSettings"));
const MyLeaveRequests = React.lazy(() => import("./MyLeaveRequests"));
const LeaveApproval = React.lazy(() => import("./LeaveApproval"));
const NotificationRules = React.lazy(() => import("./NotificationRules"));
const ShiftTemplates = React.lazy(() => import("./ShiftTemplatesPage"));
const PublicHolidays = React.lazy(() => import("./PublicHolidays"));
const LeaveDetail = React.lazy(() => import("./LeaveDetail"));
const OrphanedCheckOuts = React.lazy(() => import("./OrphanedCheckOuts"));

/**
 * Shifts Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (shift/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "edit", "create")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/shift/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 *
 * Permission levels:
 * - view: View shifts, roster, my shifts, open shifts, employee hours
 * - create: Create new shifts, duplicate shifts
 * - edit: Update shifts, publish, cancel, assign employees, manage assignments
 * - delete: Delete shifts (admin only)
 */
const shiftsModuleConfig: ModuleConfig = {
    // Module Identity
    id: "shifts",
    name: "Shift Management",
    description: "Schedule and manage employee work shifts with assignments and check-ins",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 5,
    icon: "⏰",
    color: "#805AD5",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/shifts",
            component: ShiftsList,
            permissionLevel: "view",
            useLayout: true,
        },
        {
            path: "/shifts/roster",
            component: RosterDashboard,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { noContainer: true }, // Roster needs full width
        },
        {
            path: "/shifts/my-shifts",
            component: MyShifts,
            // No permissionLevel — page handles auth inline with UnifiedLoginModal
            // so employees arriving from SMS links get a seamless login → redirect flow
            useLayout: true,
        },
        {
            path: "/shifts/available",
            component: OpenShifts,
            permissionLevel: "self",
            useLayout: true,
        },
        {
            path: "/shifts/employee-hours",
            component: EmployeeHours,
            permissionLevel: "view",
            useLayout: true,
            layoutOptions: { noContainer: true }, // Hours grid needs full width
        },
        {
            path: "/shifts/new",
            component: NewShift,
            permissionLevel: "create",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/shifts/settings",
            component: ShiftSettings,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.md" },
        },
        {
            path: "/shifts/orphaned",
            component: OrphanedCheckOuts,
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.xl" },
        },
        {
            path: "/shifts/leave/my-requests",
            component: MyLeaveRequests,
            permissionLevel: "self",
            useLayout: true,
        },
        {
            path: "/shifts/leave/approve",
            component: LeaveApproval,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/shifts/notification-rules",
            component: NotificationRules,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/shifts/templates",
            component: ShiftTemplates,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/shifts/public-holidays",
            component: PublicHolidays,
            permissionLevel: "edit",
            useLayout: true,
        },
        {
            path: "/shifts/leave/:id",
            component: LeaveDetail,
            permissionLevel: "self",
            useLayout: true,
        },
        {
            path: "/shifts/edit/:id",
            component: NewShift, // Reuse NewShift component for editing
            permissionLevel: "edit",
            useLayout: true,
            layoutOptions: { maxW: "container.lg" },
        },
        {
            path: "/shifts/:id",
            component: ShiftDetails,
            permissionLevel: "self",
            useLayout: true,
        }
    ],

    // Navigation - permissionLevel resolved from backend
    navigation: [
        {
            label: "My Shifts",
            path: "/shifts/my-shifts",
            icon: "👤",
            permissionLevel: "self",
        },
        {
            label: "All Shifts",
            path: "/shifts",
            icon: "⏰",
            permissionLevel: "view",
        },
        {
            label: "Roster Dashboard",
            path: "/shifts/roster",
            icon: "📊",
            permissionLevel: "view",
        },
        {
            label: "Employee Hours",
            path: "/shifts/employee-hours",
            icon: "⏱️",
            permissionLevel: "view",
        },
        {
            label: "Create Shift",
            path: "/shifts/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Today's Shifts",
            path: "/shifts?filter=today",
            icon: "📅",
            permissionLevel: "view",
        },
        {
            label: "Open Shifts",
            path: "/shifts?filter=open",
            icon: "👥",
            permissionLevel: "view",
        },
        {
            label: "Pick Up Shifts",
            path: "/shifts/available",
            icon: "🙋",
            permissionLevel: "self",
        },
        {
            label: "My Leave Requests",
            path: "/shifts/leave/my-requests",
            icon: "🏖️",
            permissionLevel: "self",
        },
        {
            label: "Leave Approval",
            path: "/shifts/leave/approve",
            icon: "✅",
            permissionLevel: "edit",
        },
        {
            label: "Notification Rules",
            path: "/shifts/notification-rules",
            icon: "🔔",
            permissionLevel: "edit",
        },
        {
            label: "Shift Templates",
            path: "/shifts/templates",
            icon: "📋",
            permissionLevel: "edit",
        },
        {
            label: "Public Holidays",
            path: "/shifts/public-holidays",
            icon: "🎌",
            permissionLevel: "edit",
        },
        {
            label: "Orphaned Check-Outs",
            path: "/shifts/orphaned",
            icon: "⚠️",
            permissionLevel: "edit",
        },
        {
            label: "Shift Settings",
            path: "/shifts/settings",
            icon: "⚙️",
            permissionLevel: "edit",
        }
    ],

    // Empty permissions - resolved from backend modulePermissions query
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: []
    },

    // Quick Actions
    quickActions: [
        {
            label: "My Shifts",
            path: "/shifts/my-shifts",
            icon: "👤",
            description: "View your assigned shifts"
        },
        {
            label: "Roster Dashboard",
            path: "/shifts/roster",
            icon: "📊",
            description: "Calendar view of all shifts"
        },
        {
            label: "Employee Hours",
            path: "/shifts/employee-hours",
            icon: "⏱️",
            description: "View hours by employee for payroll"
        },
        {
            label: "Create Shift",
            path: "/shifts/new",
            icon: "➕",
            description: "Schedule a new shift"
        },
        {
            label: "Today's Shifts",
            path: "/shifts?filter=today",
            icon: "📅",
            description: "View today's shifts"
        },
        {
            label: "Open Shifts",
            path: "/shifts?filter=open",
            icon: "👥",
            description: "View shifts needing staff"
        },
        {
            label: "Pick Up Shifts",
            path: "/shifts/available",
            icon: "🙋",
            description: "Staff: pick up available shifts"
        }
    ]
};

export default shiftsModuleConfig;
